<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :disabled="!isSmtpEnabled"
    @change="editSingleSettingHandler"
  >
    <template #smtp_security.input.inner="{name, value, setValue}">
      <v-row no-gutters>
        <v-col cols="12">
          <v-radio-group :value="value" @change="setValue" :disabled="!isSmtpEnabled">
            <v-radio v-for="item in encryptionItems" :key="item.translationPath" :value="item.value || 'null'">
              <template #label>
                <v-subheader
                  class="text-body-2 pa-0"
                  :class="[radioSubheaderColor]"
                  style="height: auto;"
                  v-text="getRadioLabel(item)"
                >
                </v-subheader>
              </template>
            </v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
    </template>
    <template #postForm>
      <v-row no-gutters class="align-baseline" v-if="isFormValid">
        <v-col cols="3">
          <data-input v-bind="emailToInputParams" v-model="address_to" :disabled="!isSmtpEnabled"> </data-input>
        </v-col>
        <v-col cols="8" class="pl-10">
          <v-row no-gutters class="justify-start">
            <template v-if="!isMailSent">
              <v-col cols="auto">
                <base-button
                  :disabled="!isValidMail"
                  @click="sendMailHandler"
                  translation-path="admin.sendTestMail"
                  :loading="loading"
                ></base-button>
              </v-col>
            </template>
            <template v-else>
              <v-col>
                <v-row no-gutters>
                  <v-col cols="auto">
                    <v-icon color="success">
                      mdi-check
                    </v-icon>
                  </v-col>
                  <v-col cols="auto" class="success--text">
                    {{ $i18n.t('admin.mailSent') }}
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="auto" class="pl-5">
                    <link-underlined @click="sendMailHandler" translation-path="admin.sendAgain"> </link-underlined>
                  </v-col>
                </v-row>
              </v-col>
            </template>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import LinkUnderlined from '@/components/LinkUnderlined';

export default {
  name: 'IntegrationsMail',
  components: {
    AdminWrapper,
    DataInput,
    LinkUnderlined,
  },
  data: () => ({
    // values: {},
    loading: false,
    isMailSent: false,
    // isFormValid: true,
    encryptionItems: [
      {
        translationPath: 'admin.noEncryption',
        value: 'NONE',
      },
      {
        translationPath: null,
        value: 'SSL',
      },
      {
        translationPath: null,
        value: 'TLS',
      },
    ],
    emailToInputParams: {
      dataType: {
        name: 'text',
      },
      hint: 'admin.emailTo',
    },
    address_to: undefined,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.SMTP.verbose_name'),
    radioSubheaderColor() {
      return this.isSmtpEnabled ? 'font--text' : 'text--disabled';
    },
    inputs() {
      return [
        {
          name: 'smtp_module_enabled',
          hasSuffix: false,
          straightText: this.additionalModuleName,
          canBeDisabled: false,
          dataType: {
            name: 'switch',
          },
          subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
        },
        {
          name: 'smtp_host',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.smtpServerAddress',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_user',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.login',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_sender',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.userName',
          subtitle: 'admin.displayedUserName',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_password',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          type: 'password',
          translationPath: 'admin.password',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_port',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.port',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_security',
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.encryption',
          align: 'baseline',
        },
      ];
    },
    settingsValues: get('admin/settingsValues'),
    filteredSettingValues() {
      const viewKeys = this.inputs.map(input => input.name);
      const filteredKeys = Object.keys(this.settingsValues).filter(key => viewKeys.includes(key));
      return filteredKeys.reduce((total, current) => {
        return { ...total, [current]: this.settingsValues[current] };
      }, {});
    },
    appName: get('admin/settingsValues@app_name'),
    isSmtpEnabled: get('admin/settingsValues@smtp_module_enabled'),
    isFormValid() {
      return Object.keys(this.filteredSettingValues).every(key => this.filteredSettingValues[key]);
    },
    isValidMail() {
      return this.$_validateEmail(this.address_to);
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    sendMail: call('admin/sendMail'),
    editSingleSettingHandler(value, name) {
      if (name === 'smtp_module_enabled') {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      }
    },
    async sendMailHandler() {
      console.log('~ sendMailHandler');
      try {
        this.loading = true;
        await this.sendMail({
          address_to: this.address_to,
          content: this.$i18n.t('admin.emailTestMessage', { app_name: this.appName }),
          subject: this.$i18n.t('admin.emailTestSubject'),
        });
        setTimeout(() => {
          this.loading = false;
          this.isMailSent = true;
        }, 500);
      } catch (error) {
        this.loading = false;
      }
    },
    getRadioLabel(item) {
      return item.translationPath ? this.$i18n.t(item.translationPath) : item.value.toUpperCase();
    },
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(all, 2px);
  border-style: dashed;
}
</style>
